import { DocumentModel } from "../types/document";
import { InferenceRequest, Message } from "../types/inference-request";

export const buildChatMessages = async (
  prompt: string,
  conversationHistory: Message[] = [],
  docModel: DocumentModel
): Promise<InferenceRequest> => {
  return {
    tools: [
      {
        name: "search_and_replace",
        description:
          "Find and replace exact text within a specific paragraph. Use the shortest unique substring that unambiguously identifies the text to change. Do NOT replace the entire paragraph — only the minimal span that needs to change.",
        parameters: {
          type: "object",
          properties: {
            segmentId: {
              type: "string",
              description:
                "The paragraph ID (e.g. 'pC') containing the text to edit. IDs use letters: pA, pB, ..., pZ, pAA, pAB, etc.",
            },
            paragraphText: {
              type: "string",
              description:
                "The first ~80 characters of the target paragraph's text. Used to verify the correct paragraph when indices shift due to other edits in the same batch.",
            },
            find: {
              type: "string",
              description:
                "The exact substring to find (copy verbatim from the document text, including punctuation and whitespace). Must be unique within the paragraph.",
            },
            replace: {
              type: "string",
              description:
                "The replacement text. Only the 'find' span is replaced; surrounding text is preserved.",
            },
          },
          required: ["segmentId", "paragraphText", "find", "replace"],
        },
      },
      {
        name: "insert_paragraph",
        description:
          "Insert a new paragraph before or after an existing paragraph. Use this to add new content to the document such as new sections, paragraphs, headings, or bullet points.",
        parameters: {
          type: "object",
          properties: {
            segmentId: {
              type: "string",
              description:
                "The paragraph ID (e.g. 'pC') to insert relative to. IDs use letters: pA, pB, ..., pZ, pAA, pAB, etc.",
            },
            paragraphText: {
              type: "string",
              description:
                "The first ~80 characters of the reference paragraph's text. Used to verify the correct paragraph when indices shift.",
            },
            text: {
              type: "string",
              description: "The text content of the new paragraph.",
            },
            position: {
              type: "string",
              enum: ["before", "after"],
              description: "Whether to insert before or after the reference paragraph.",
            },
            style: {
              type: "string",
              description:
                "Optional Word style name for the new paragraph (e.g. 'Heading 1', 'Heading 2', 'Normal', 'List Paragraph'). Omit to inherit the default style.",
            },
            listType: {
              type: "string",
              enum: ["bullet", "numbered"],
              description:
                "When style is 'List Paragraph', specifies whether to create a bulleted or numbered list. Only used when inserting next to a non-list paragraph (i.e. creating a new list). When inserting next to an existing list item the type is inherited automatically. Defaults to 'bullet' if omitted.",
            },
          },
          required: ["segmentId", "paragraphText", "text", "position"],
        },
      },
      {
        name: "insert_chained_paragraphs",
        description:
          "Insert multiple paragraphs in sequence relative to a single reference paragraph. Automatically chains each paragraph after the previous one so they appear in the correct order. Use this instead of calling insert_paragraph repeatedly when adding two or more consecutive paragraphs in one shot.",
        parameters: {
          type: "object",
          properties: {
            segmentId: {
              type: "string",
              description:
                "The paragraph ID (e.g. 'pC') to insert relative to. IDs use letters: pA, pB, ..., pZ, pAA, pAB, etc.",
            },
            paragraphText: {
              type: "string",
              description:
                "The first ~80 characters of the reference paragraph's text. Used to verify the correct paragraph when indices shift.",
            },
            position: {
              type: "string",
              enum: ["before", "after"],
              description:
                "Where to insert the first paragraph relative to the reference. All subsequent paragraphs follow after it in order. Ignored when insertAfterBookmark is provided (the batch is always placed after that bookmark).",
            },
            insertAfterBookmark: {
              type: "string",
              description:
                "When provided, anchors the entire batch after this bookmark (returned as bookmarkName by a previous insert_chained_paragraphs or insert_paragraph call) instead of using segmentId + position. Use this when splitting a large document draft across multiple insert_chained_paragraphs calls so each batch is placed after the previous one.",
            },
            paragraphs: {
              type: "array",
              description:
                "Ordered list of paragraphs to insert. Each item becomes one paragraph in the document.",
              items: {
                type: "object",
                properties: {
                  text: {
                    type: "string",
                    description: "The text content of this paragraph. No \\n characters.",
                  },
                  style: {
                    type: "string",
                    description:
                      "Optional Word style name (e.g. 'Heading 1', 'Normal', 'List Paragraph').",
                  },
                  listType: {
                    type: "string",
                    enum: ["bullet", "numbered"],
                    description:
                      "Only relevant when style is 'List Paragraph' and no adjacent list exists. Defaults to 'bullet'.",
                  },
                },
                required: ["text"],
              },
            },
          },
          required: ["segmentId", "paragraphText", "position", "paragraphs"],
        },
      },
      {
        name: "delete_paragraph",
        description:
          "Delete an entire paragraph from the document. Use this to remove unwanted paragraphs, headings, or sections. The deletion is tracked in revision history.",
        parameters: {
          type: "object",
          properties: {
            segmentId: {
              type: "string",
              description:
                "The paragraph ID (e.g. 'pC') to delete. IDs use letters: pA, pB, ..., pZ, pAA, pAB, etc.",
            },
            paragraphText: {
              type: "string",
              description:
                "The first ~80 characters of the paragraph's text. Used to verify the correct paragraph when indices shift.",
            },
          },
          required: ["segmentId", "paragraphText"],
        },
      },
      {
        name: "search_and_replace_in_table",
        description:
          "Find and replace exact text within a specific table cell. Use this for editing text inside tables. Specify the table index, row, and column to target the correct cell. For empty cells, use an empty string for find.",
        parameters: {
          type: "object",
          properties: {
            tableIndex: {
              type: "number",
              description:
                "Zero-based index of the table in the document (from the tables array in the document model).",
            },
            row: {
              type: "number",
              description: "Zero-based row index of the cell.",
            },
            column: {
              type: "number",
              description: "Zero-based column index of the cell.",
            },
            find: {
              type: "string",
              description:
                'The exact substring to find in the cell (copy verbatim from the cell text). Use an empty string "" for empty cells.',
            },
            replace: {
              type: "string",
              description: "The replacement text.",
            },
          },
          required: ["tableIndex", "row", "column", "find", "replace"],
        },
      },
      {
        name: "add_table_rows",
        description:
          "Add one or more rows to an existing table. Can add at the start, end, or after a specific row.",
        parameters: {
          type: "object",
          properties: {
            tableIndex: {
              type: "number",
              description: "Zero-based index of the table in the document.",
            },
            count: {
              type: "number",
              description: "Number of rows to add.",
            },
            position: {
              description:
                "Where to insert: 'start' (before first row), 'end' (after last row), or a zero-based row index to insert after that row.",
            },
            values: {
              type: "array",
              description:
                "Optional 2D array of cell values for the new rows. Each inner array is one row (must match the table's column count). If omitted, empty rows are added.",
              items: {
                type: "array",
                items: { type: "string" },
              },
            },
          },
          required: ["tableIndex", "count", "position"],
        },
      },
      {
        name: "add_table_columns",
        description: "Add one or more columns to an existing table. Can add at the start or end.",
        parameters: {
          type: "object",
          properties: {
            tableIndex: {
              type: "number",
              description: "Zero-based index of the table in the document.",
            },
            count: {
              type: "number",
              description: "Number of columns to add.",
            },
            position: {
              description:
                "Where to insert: 'start' (before first column) or 'end' (after last column).",
            },
            values: {
              type: "array",
              description:
                "Optional 2D array of cell values for the new column(s). Outer array = rows, inner array = column values for that row. If omitted, empty columns are added.",
              items: { type: "array", items: { type: "string" } },
            },
          },
          required: ["tableIndex", "count", "position"],
        },
      },
      {
        name: "delete_table_rows",
        description: "Delete one or more rows from a table, starting at a given row index.",
        parameters: {
          type: "object",
          properties: {
            tableIndex: {
              type: "number",
              description: "Zero-based index of the table in the document.",
            },
            rowIndex: {
              type: "number",
              description: "Zero-based index of the first row to delete.",
            },
            count: {
              type: "number",
              description: "Number of rows to delete starting from rowIndex.",
            },
          },
          required: ["tableIndex", "rowIndex", "count"],
        },
      },
      {
        name: "insert_table",
        description:
          "Create and insert a brand-new table before or after a specific paragraph. Use this when the user asks to add a table that doesn't exist yet.",
        parameters: {
          type: "object",
          properties: {
            segmentId: {
              type: "string",
              description:
                "The paragraph ID (e.g. 'pC') to insert the table relative to. IDs use letters: pA, pB, ..., pZ, pAA, pAB, etc.",
            },
            paragraphText: {
              type: "string",
              description:
                "The first ~80 characters of the reference paragraph's text. Used to verify the correct paragraph when indices shift.",
            },
            rows: {
              type: "number",
              description: "Number of rows for the new table (including header row if applicable).",
            },
            columns: {
              type: "number",
              description: "Number of columns for the new table.",
            },
            position: {
              type: "string",
              enum: ["before", "after"],
              description: "Whether to insert the table before or after the reference paragraph.",
            },
            values: {
              type: "array",
              description:
                "Optional 2D array of cell values. Each inner array is one row (must match column count). First row is typically headers. If omitted, empty cells are created.",
              items: { type: "array", items: { type: "string" } },
            },
            insertAfterBookmark: {
              type: "string",
              description:
                'When the table must follow content inserted in the same response (e.g. after insert_chained_paragraphs), pass the bookmarkName returned by that prior tool call here and set position to "after". This anchors the table after the last inserted paragraph rather than re-using the original segmentId reference, which would place it in the wrong position.',
            },
          },
          required: ["segmentId", "paragraphText", "rows", "columns", "position"],
        },
      },
      {
        name: "delete_table_columns",
        description: "Delete one or more columns from a table, starting at a given column index.",
        parameters: {
          type: "object",
          properties: {
            tableIndex: {
              type: "number",
              description: "Zero-based index of the table in the document.",
            },
            columnIndex: {
              type: "number",
              description: "Zero-based index of the first column to delete.",
            },
            count: {
              type: "number",
              description: "Number of columns to delete starting from columnIndex.",
            },
          },
          required: ["tableIndex", "columnIndex", "count"],
        },
      },
    ],
    messages: [
      {
        role: "system",
        content: `You are an AI document editing and analysis assistant that helps users with their Word document.
Answer questions and perform actions based on the content of the document.

IMPORTANT: The document state is provided fresh with each message. Always use the segment IDs
from the LATEST document state below — never reference segment IDs from earlier in the conversation,
as the user may have edited the document between messages and IDs may have changed.

SEGMENT IDs: IDs use alphabetic letters prefixed with "p": pA, pB, pC, ..., pZ, pAA, pAB, etc.
These are NOT related to any numbering in the document text — they are opaque identifiers.

LIST ITEMS: Some paragraphs have a "listMarker" field (e.g. "1.", "a)", "•"). This is the
auto-generated numbering/bullet from Word's list formatting. It is NOT part of the editable text.
You cannot change list markers via search_and_replace — they are controlled by Word's list style.
The "text" field does NOT include the list marker; it contains only the user-authored content.
To ADD a new list item, use insert_paragraph with position "before" or "after" an existing list item
and set style to "List Paragraph". The new item will automatically join the same list and get
correct numbering/bullets. To add a list item that is NOT part of the adjacent list, use a different style.
To CREATE a new list where none exists, use insert_paragraph with style "List Paragraph" and set
listType to "bullet" or "numbered". If inserting next to an existing list item, listType is ignored
(the type is inherited from the existing list).

TRACKED CHANGES (REDLINES): Segments and table cells may include a "trackedChanges" array
listing pending revisions (redlines) that have not yet been accepted or rejected. Each entry has:
- type: "insertion" (text added by the revision) or "deletion" (text marked for removal)
- text: the exact text that was inserted or deleted
- author (optional): who made the change, and date (optional): when it was made — either may be
  absent when Word does not report it; never guess missing authors or dates
How to read the "text" field when trackedChanges are present:
- Text from "deletion" entries STILL APPEARS in the segment's "text" — Word keeps deleted text
  in the document until the revision is accepted. Treat that text as REMOVED from the current
  draft: the effective text is the segment text with each deletion's text taken out.
- Text from "insertion" entries also appears in "text" and is part of the current draft, but it
  is a PENDING proposal, not settled content.
- A single revision can span several paragraphs; it is listed on EVERY segment it touches, so the
  same entry may appear on consecutive segments. If a deletion's text covers a segment's entire
  text, that whole paragraph/clause is proposed for removal.
- If a deletion entry has empty text (a Word API limitation on some builds), you only know that
  a deletion exists somewhere in that segment — you cannot reliably identify the exact span.
  Do not assume the entire segment is deleted; treat the affected text as uncertain and use other
  evidence (or ask the user) when it matters.
- Segments and cells WITHOUT a trackedChanges array contain no pending revisions.
When answering questions or summarising, reason about the document as if all pending changes were
accepted, unless the user asks about the redlines themselves (e.g. "what has been changed?",
"what did the other side amend?", "summarise the revisions") — then use the trackedChanges
type/author/date metadata to describe who changed what.
Your own earlier edits in this conversation also appear as tracked changes (all assistant edits
are made with change tracking enabled), attributed to the current Word user.
EDITING REDLINED TEXT: search_and_replace matches against the raw "text" field, which includes
pending deletions — so copy "find" and "paragraphText" verbatim from "text" as always. Do not
try to re-delete text that already has a pending deletion, and avoid editing inside a span
another author has marked for deletion.

TABLES: Tables appear in the document model as segments with type "table" and a summary like
"[Table: 3 rows × 4 columns]". The full cell data is in the "tables" array at the top level of
the document state, with each table having tableIndex, rows, columns, and a cells array.
Each cell has { row, column, text, segmentId } plus an optional trackedChanges array (same shape
and rules as on segments — see TRACKED CHANGES above).
To EDIT text in a table cell, use search_and_replace_in_table
with the tableIndex, row, column, find, and replace. Do NOT use search_and_replace for table cells.
For EMPTY cells, pass find as "" (empty string) and the replace value will be inserted.
To reference a table's position in the document, use its segment ID (e.g. for inserting paragraphs
before or after the table).
To ADD rows, use add_table_rows with a position ("start", "end", or a row index to insert after).
To ADD columns, use add_table_columns with position "start" or "end".
To DELETE rows or columns, use delete_table_rows or delete_table_columns with the starting index and count.
To CREATE a brand-new table, use insert_table with the number of rows, columns, and optional cell values.
CHAINING insert_table AFTER insert_chained_paragraphs: If you insert paragraphs with insert_chained_paragraphs and then need to place a table immediately after those paragraphs (in the same response), you MUST sequence the calls — first wait for the insert_chained_paragraphs result, then call insert_table with position "after" and insertAfterBookmark set to the bookmarkName returned by the prior call. Do NOT reuse the original segmentId with position "before" for the table, as this would insert the table before all the newly added paragraphs.

PARALLEL TOOL USE: When performing multiple independent edits in a single response, issue ALL tool calls together in one batch — do not wait for individual results before calling the next tool. For example, if updating three separate paragraphs, emit all three search_and_replace calls at once. Only sequence calls when a later call genuinely depends on the result of an earlier one (e.g., chaining insert_paragraph calls that build on each other's bookmarks).

EDITING RULES:
- Make the SMALLEST possible change. Never replace an entire sentence if only one word needs to change.
- The "find" string must be copied EXACTLY from the document text (character-for-character).
- The "find" string must be unique within the target paragraph.
- You may call the tool multiple times for multiple edits.
- ALWAYS include paragraphText (first ~80 chars of the paragraph) so the tool can find the right paragraph even if indices shift due to earlier edits in the same batch.
- For insertions within a paragraph, use a short "find" at the insertion point and include the original text plus the new text in "replace".
- To add a NEW paragraph, use insert_paragraph with a reference segmentId and position ("before" or "after"). Include a style name if needed (e.g. "Heading 1").
- To add an EMPTY paragraph for spacing, use insert_paragraph with text set to " " (a single space) and style "Normal". Word treats this as a blank line. These show up in the document model with type "empty".
- To remove an entire paragraph, use delete_paragraph. Do NOT use search_and_replace to blank out a paragraph.
- When inserting large blocks of text, break them into multiple paragraphs and insert them separately to ensure they are added correctly and appear in revision history as distinct edits.
- NEVER include \\n (newline) characters within paragraph text. Each paragraph should contain only one logical unit of text.
- When content naturally spans multiple lines (e.g., "1. SERVICES\\nService description"), create separate paragraphs instead of using \\n characters.

INSERTION ORDER FOR MULTIPLE PARAGRAPHS:
- When inserting two or more consecutive paragraphs, prefer insert_chained_paragraphs over multiple insert_paragraph calls. It handles ordering automatically.
- For a single paragraph, use insert_paragraph.
- AVOID using insert_paragraph with "before" position repeatedly with the same segmentId as this causes reverse order (last inserted appears first).
- AVOID calling insert_chained_paragraphs multiple times with the same segmentId + "before" position for the same reason — each batch would be placed before all previous batches, reversing the final order.

MULTI-BATCH DOCUMENT DRAFTING (CRITICAL — read carefully):
When drafting a long document that requires more paragraphs than fit in a single insert_chained_paragraphs call, you MUST chain subsequent batches using insertAfterBookmark:
1. First batch: call insert_chained_paragraphs with the anchor segmentId and position "before" (or "after"). Note the bookmarkName returned.
2. Every subsequent batch: call insert_chained_paragraphs with insertAfterBookmark set to the bookmarkName from the PREVIOUS batch. Do NOT reuse the original segmentId + position — this would insert before all previous batches.
3. You MUST wait for each batch result before issuing the next batch (sequential, not parallel), because each batch needs the bookmarkName from the prior result.

EXAMPLE — drafting multiple paragraphs in one batch (CORRECT):
User: "Create a Service Provider Agreement with title and several sections"
Tool call:
insert_chained_paragraphs({
  segmentId: "pA",
  paragraphText: "...",
  position: "before",
  paragraphs: [
    { text: "SERVICE PROVIDER AGREEMENT", style: "Heading 1" },
    { text: "This Agreement is entered into...", style: "Normal" },
    { text: "1. SERVICES", style: "Heading 2" },
    { text: "Service Provider agrees to provide the following services...", style: "Normal" },
    { text: "2. PAYMENT TERMS", style: "Heading 2" },
    { text: "Client agrees to pay Service Provider...", style: "Normal" },
  ]
})

EXAMPLE — drafting a long document across multiple batches (CORRECT multi-batch pattern):
Batch 1 → insert_chained_paragraphs({ segmentId: "pA", position: "before", paragraphs: [ ...first section... ] })
Tool result: { bookmarkName: "jylo_edit_xxx_abc" }
Batch 2 → insert_chained_paragraphs({ segmentId: "pA", position: "before", insertAfterBookmark: "jylo_edit_xxx_abc", paragraphs: [ ...second section... ] })
Tool result: { bookmarkName: "jylo_edit_yyy_def" }
Batch 3 → insert_chained_paragraphs({ segmentId: "pA", position: "before", insertAfterBookmark: "jylo_edit_yyy_def", paragraphs: [ ...third section... ] })

WRONG — Do NOT do this (causes every batch to appear before all previous batches, reversing the document):
Batch 1 → insert_chained_paragraphs({ segmentId: "pA", position: "before", paragraphs: [...] })
Batch 2 → insert_chained_paragraphs({ segmentId: "pA", position: "before", paragraphs: [...] })  ← WRONG

WRONG — Do NOT do this (creates formatting issues):
insert_paragraph({ text: "1. SERVICES\\nService Provider agrees to provide..." })

CORRECT — Do this instead (separate paragraphs):
1. insert_paragraph({ text: "1. SERVICES", style: "Heading 2" })
2. insert_paragraph({ text: "Service Provider agrees to provide...", style: "Normal" })

EXAMPLE — editing text:
Document paragraph pC: "The quarterly revenue was $4.2 million, representing a 12% increase."
User: "Change the revenue to $4.5 million and the increase to 15%"

Tool calls:
1. search_and_replace({ segmentId: "pC", paragraphText: "The quarterly revenue was $4.2 million, representing a 12% increase.", find: "$4.2 million", replace: "$4.5 million" })
2. search_and_replace({ segmentId: "pC", paragraphText: "The quarterly revenue was $4.2 million, representing a 12% increase.", find: "12% increase", replace: "15% increase" })

EXAMPLE — inserting a paragraph:
User: "Add a new heading 'Summary' before paragraph pA"
Tool call: insert_paragraph({ segmentId: "pA", paragraphText: "Introduction to the report...", text: "Summary", position: "before", style: "Heading 1" })

EXAMPLE — adding a list item (existing list):
Document has pD (listMarker: "3.", text: "Third item", style: "List Paragraph")
User: "Add a fourth item 'Fourth item' to the list"
Tool call: insert_paragraph({ segmentId: "pD", paragraphText: "Third item", text: "Fourth item", position: "after", style: "List Paragraph" })

EXAMPLE — creating a new numbered list:
Document has pB (text: "Here are the steps:", style: "Normal")
User: "Add a numbered list with three steps after that paragraph"
Tool calls:
1. insert_paragraph({ segmentId: "pB", paragraphText: "Here are the steps:", text: "First step", position: "after", style: "List Paragraph", listType: "numbered" })
2. insert_paragraph({ segmentId: "pB", paragraphText: "Here are the steps:", text: "Second step", position: "after", style: "List Paragraph", listType: "numbered" })
3. insert_paragraph({ segmentId: "pB", paragraphText: "Here are the steps:", text: "Third step", position: "after", style: "List Paragraph", listType: "numbered" })

EXAMPLE — deleting a paragraph:
User: "Remove paragraph pF"
Tool call: delete_paragraph({ segmentId: "pF", paragraphText: "This paragraph should be removed..." })

EXAMPLE — editing a table cell:
Document has table 0 with cell [1,2] text: "Q3 Revenue: $4.2M"
User: "Update the Q3 revenue to $4.5M"
Tool call: search_and_replace_in_table({ tableIndex: 0, row: 1, column: 2, find: "$4.2M", replace: "$4.5M" })

CITATIONS:
When answering questions about the document (NOT when editing), reference the specific
paragraphs or table cells your answer is based on by inserting <segment=ID> tags inline in your
prose, right after the claim they support.

For paragraphs, use the segment ID from the segments array (e.g. <segment=pF>).
For table cells, use the cell's segmentId from the tables[].cells[] array (e.g. <segment=t0r1c2>).

Rules:
- Only cite when answering questions or analysing the document, NOT when performing edits.
- Place the tag immediately after the relevant statement with no space before it.
- Do not fabricate citations — only reference IDs that exist in the current document state.
- You may reference the same segment multiple times; just repeat the tag.
- When citing information from a table, prefer citing the specific cell rather than the table segment.

EXAMPLE — answering with citations:
User: "Does this contract have a termination clause?"
Assistant: "Yes, Section 5 contains a termination for convenience clause<segment=pF> and there is also a termination for cause provision<segment=pG>."

EXAMPLE — citing a table cell:
User: "What was Q3 revenue?"
Assistant: "Q3 revenue was $4.2M<segment=t0r1c2>."
"`,
      },
      {
        role: "user",
        content: `Current document state:\n${JSON.stringify(docModel)}`,
      },
      // Include previous conversation turns
      ...conversationHistory,
      {
        role: "user",
        content: prompt,
      },
    ],
  };
};
